import { useState } from 'react';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import * as R from 'ramda';

import {
  requestFhwaElementDeletion,
  requestRootAssetBatchCreateFHWA,
} from '@atom/actions/inventoryAssetActions';
import * as inventorySchemaActions from '@atom/actions/inventorySchemaActions';
import FontIconButton from '@atom/components/common/FontIconButton';
import percentageIcon from '@atom/components/common/svgIcons/percentageIcon.svg';
import { Icon } from '@atom/mui';
import { fhwaFormModule } from '@atom/selectors/formModuleSelectors';
import colors from '@atom/styles/colors';
import { FormInstanceFieldItem } from '@atom/types/form';
import { InventorySchemaItem } from '@atom/types/inventory';
import { MediaItem } from '@atom/types/media';

import FhwaAddElementModal from './FhwaAddElementModal';
import FhwaFieldRow from './FhwaFieldRow';
import { FHWA_PREVIEW_MOCK } from './fhwaPreviewMock';

import classes from '../formInstance.module.css';

const styles = {
  icon: {
    padding: '0px',
    height: '24px',
    width: '24px',
  },
  addIcon: {
    marginLeft: '1rem',
    padding: '0px',
    height: '24px',
    width: '24px',
  },
};

interface Props {
  field: FormInstanceFieldItem;
  fieldId: string;
  rootSchema: InventorySchemaItem;
  formInstanceId: string;
  preview: boolean;
  isEditable: boolean;
  media: MediaItem[];
  renameMedia: (mediaId: string, subjectId: string, name: string) => void;
  inventoryAssetId: string;
  isUploadDisabled: boolean;
  uploadFiles: (
    files?: any[],
    subjectId?: string,
    subjectType?: string,
  ) => void;
  removeFile: (mediaId: string, subjectId: string) => void;
  progressiveUpdateFormInstanceFhwa: (
    fieldId: string,
    assetId: string,
    body: Object,
    elementPath: any[],
  ) => void;
}

const FhwaField = ({
  preview,
  field,
  fieldId,
  media,
  uploadFiles,
  removeFile,
  renameMedia,
  isEditable,
  isUploadDisabled,
  progressiveUpdateFormInstanceFhwa,
  inventoryAssetId,
  rootSchema,
  formInstanceId,
}: Props) => {
  const dispatch = useDispatch();
  const [csType, setCsType] = useState<string>('');

  const swapType = () => {
    setCsType(csType === '%' ? '' : '%');
  };

  const onBatchCreate = (payload: Object) => {
    dispatch(
      requestRootAssetBatchCreateFHWA({
        ...payload,
        formInstanceId,
        rootAssetId: inventoryAssetId,
      }),
    );
  };

  const onElementDelete = (assetId: string): boolean => {
    dispatch(
      requestFhwaElementDeletion({
        assetId,
        formInstanceId,
      }),
    );

    return true;
  };

  const suffix = csType === '%' ? '(%)' : '';

  const swapIcon =
    csType === '%' ? (
      <FontIconButton
        style={styles.icon}
        onClick={swapType}
        color={colors.neutral.gray}
        icon="looks_one"
      />
    ) : (
      <img
        className={classes.percentageIcon}
        src={percentageIcon}
        alt="Help"
        onClick={swapType}
      />
    );

  const fhwaForm = preview ? FHWA_PREVIEW_MOCK : field;
  const order = R.pathOr([], ['order'], fhwaForm);
  const elements = R.pathOr({}, ['elements'], fhwaForm);

  const existingSchemaIdList = order.map(assetId => elements[assetId].schemaId);
  const existingSchemaIds = existingSchemaIdList.reduce((id, index) => {
    id[index] = true;
    return id;
  }, {});

  return (
    <div className={classes.fhwaContainer}>
      <div className={classes.fhwaHeader}>
        <div className={classes.fhwaIcons}>
          <Icon color={colors.brand.blue}>{fhwaFormModule.icon}</Icon>
          <div className={classes.fhwaTitle}>{fhwaFormModule.title}</div>
        </div>
        <div className={classes.fhwaIcons}>
          {swapIcon}
          {isEditable && (
            <FhwaAddElementModal
              title="Add elements to asset"
              assetId={inventoryAssetId}
              schemaId={rootSchema.id}
              assetName={rootSchema.name}
              inventorySchemaActions={inventorySchemaActions}
              // @ts-ignore
              onBatchCreateAction={onBatchCreate}
              existingSchemaIds={existingSchemaIds}
              isFhwaForm
            />
          )}
        </div>
      </div>
      <div className={classes.fhwaTableHeader}>
        <div className={clsx(classes.elementColumn, classes.header)}>
          sub item
        </div>
        <div className={clsx(classes.largeColumn, classes.environment)}>
          environment
        </div>
        <div className={classes.largeColumn}>total qty.</div>
        <div className={classes.cs1HeaderColumn}>{`cs1 ${suffix}`}</div>
        <div className={classes.normalColumn}>{`cs2 ${suffix}`}</div>
        <div className={classes.normalColumn}>{`cs3 ${suffix}`}</div>
        <div className={classes.normalColumn}>{`cs4 ${suffix}`}</div>
        <div className={classes.attachColumn}>photos</div>
        <div className={clsx(classes.attachColumn, classes.right)}>notes</div>
        <div className={clsx(classes.attachColumn, classes.additional)} />
      </div>
      <div className={classes.fhwaRowsContainer}>
        {order.map((assetId: string, index: number) => {
          const fieldRowMedia = media.filter(
            medium => medium.fieldId === assetId,
          );
          const row = (
            <div className={classes.fhwaRowBlock} key={index}>
              <FhwaFieldRow
                csType={csType}
                key={`${assetId}-${index}`}
                element={elements[assetId]}
                assetId={assetId}
                fieldId={fieldId}
                isEditable={isEditable}
                isUploadDisabled={isUploadDisabled}
                uploadFiles={uploadFiles}
                media={fieldRowMedia}
                renameMedia={renameMedia}
                removeFile={removeFile}
                progressiveUpdateFormInstanceFhwa={
                  progressiveUpdateFormInstanceFhwa
                }
                indentLevel={0}
                elementPath={[assetId]}
                onBatchCreate={onBatchCreate}
                onElementDelete={onElementDelete}
              />
            </div>
          );

          const isFhwaValid = !R.isNil(elements) && !R.isEmpty(elements);

          return isFhwaValid ? row : <div />;
        })}
      </div>
    </div>
  );
};

export default FhwaField;
